import React, { useMemo, useState } from 'react';
import WorkCard from '../components/WorkCard';
import { useProjects } from '../hooks/useProjects';
import { usePageContent } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';

export default function Work() {
    const { projects: projectsList, loading, error } = useProjects();
    const { content: workContent } = usePageContent('work');
    const [active, setActive] = useState('All');

    const heroCopy = {
        eyebrow: workContent?.eyebrow || 'Projects',
        heading: workContent?.heading || 'Selected work, with the context that didn\'t fit on a resume.',
        description:
            workContent?.description ||
            'Six bets across marketplaces, fintech, and B2C - most shipped, all taught me something I now carry into the next one.',
    };

    usePageTitle('Work');

    // Build categories dynamically from projects
    const categories = useMemo(() => {
        const cats = new Set(['All']);
        projectsList.forEach(p => {
            if (p.category) cats.add(p.category);
        });
        return Array.from(cats);
    }, [projectsList]);

    // Filter projects by category
    const filtered = useMemo(() => {
        if (active === 'All') return projectsList;
        return projectsList.filter((p) => p.category === active);
    }, [active, projectsList]);

    if (loading) {
        return <div className="container-page pt-20">Loading projects...</div>;
    }

    if (error || projectsList.length === 0) {
        return (
            <div className="container-page pt-20">
                <p className="text-gray-600">No projects found. Add some in the Admin Panel!</p>
            </div>
        );
    }

    return (
        <>
            <section className="container-page pt-12 md:pt-28 pb-8">
                <p className="eyebrow mb-6">{heroCopy.eyebrow}</p>
                <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tightest text-fg max-w-[20ch]">
                    {heroCopy.heading}
                </h1>
                <p className="mt-6 max-w-prose text-[16px] text-fg-muted leading-relaxed">
                    {heroCopy.description}
                </p>
            </section>

            <section className="container-page pb-6">
                <div className="flex flex-wrap items-center gap-2 border-y border-line py-4">
                    {categories.map((c) => {
                        const isActive = c === active;
                        return (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setActive(c)}
                                className={
                                    'inline-flex items-center px-3 py-1.5 rounded-full text-[13px] transition-colors border ' +
                                    (isActive
                                        ? 'bg-accent text-bg border-transparent'
                                        : 'border-line text-fg-muted hover:text-fg hover:border-fg')
                                }
                            >
                                {c}
                                <span className="ml-2 font-mono text-[11px] opacity-70">
                                    {c === 'All' ? projectsList.length : projectsList.filter((p) => p.category === c).length}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </section>

            <section className="container-page pb-14 md:pb-24">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {filtered.map((p, idx) => (
                        <WorkCard
                            key={idx}
                            year={p.year}
                            company={p.company}
                            title={p.title}
                            description={p.description}
                            tags={p.tags}
                            link={p.link}
                            applicationLink={p.applicationLink}
                            previews={p.previews || []}
                            fallbackThumbnail={p.thumbnailUrl || ""}
                        />
                    ))}
                </div>
            </section>
        </>
    );
}
